import moment from 'moment'
import i18n from '@/i18n/i18nMain'
import store from '@/store/mainStore'

/**
 * Date / time formatters extracted from the global `Vue.filter(...)` block
 * formerly registered in `src/main.js`. Vue 3 removes template filters; these
 * are now plain functions imported per call site as `{{ formatDate(value) }}`.
 *
 * Behaviour preserved 1:1 with the legacy filters. Where the filter used
 * `this.$t` or `store.state.userStore.user_settings.language`, those reads
 * are now done through directly-imported singletons (i18n + store) so the
 * call signature stays bare-arg friendly for the codemod.
 */

const userLanguage = () =>
    store?.state?.userStore?.user_settings?.language || 'en'

export function formatDateSubmission(value) {
    if (typeof value !== 'string' || /^[0-9]+$/.test(value)) {
        value = +value * 1000
    }
    if (!value) return ''
    const dateObj = moment.utc(value).local()

    // English weekday name as the i18n key (keys are in English).
    const dayOfWeek = dateObj.locale('en').format('dddd')

    const oneYearFromNow = moment().add(1, 'year')
    const isMoreThanOneYearAway = dateObj.isAfter(oneYearFromNow)

    if (isMoreThanOneYearAway) {
        return dateObj.format('DD/MM/YYYY')
    }
    const time = dateObj.format('HH:mm')
    // vue-i18n@9 in legacy mode exposes the translator as `i18n.global.t`,
    // not `i18n.t` (the latter is the Composition-mode shape).
    return `${i18n.global.t(`weekdays.${dayOfWeek}`)} ${dateObj.format('DD/MM')} ${time}`
}

export function formatDate(value) {
    if (typeof value !== 'string' || /^[0-9]+$/.test(value)) {
        value = +value * 1000
    }
    if (!value) return ''
    if (userLanguage() === 'en') {
        return moment.utc(value).local().format('YYYY-MM-DD')
    }
    return moment.utc(value).local().format('DD/MM/YYYY')
}

export function formatTime(value) {
    if (typeof value !== 'string' || /^[0-9]+$/.test(value)) {
        value = +value * 1000
    }
    if (!value) return ''
    return moment.utc(value).local().format('HH:mm')
}

export function formatDateAndTime(value, language) {
    if (!value) return ''
    if (
        (typeof value !== 'string' || /^[0-9]+$/.test(value)) &&
        value.toString().length < 13
    ) {
        value = +value * 1000
    }
    if (!value) return ''

    if (language === 'en') return moment.utc(value).local().format('YYYY-MM-DD HH:mm')
    if (language === 'da') return moment.utc(value).local().format('DD/MM/YYYY HH:mm')

    if (userLanguage() === 'da') {
        return moment.utc(value).local().format('DD/MM/YYYY HH:mm')
    }
    return moment.utc(value).local().format('YYYY-MM-DD HH:mm')
}

export function toHHMMSS(value) {
    const secNum = parseInt(value, 10)
    let hours = Math.floor(secNum / 3600)
    let minutes = Math.floor((secNum - hours * 3600) / 60)
    let seconds = secNum - hours * 3600 - minutes * 60

    if (hours < 10) hours = `0${hours}`
    if (minutes < 10) minutes = `0${minutes}`
    if (seconds < 10) seconds = `0${seconds}`
    return `${hours}:${minutes}:${seconds}`
}
